import { useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Icon,
  LoadingState,
  StatePanel,
  V2Alert,
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2ConfirmDialog,
  V2DataGrid,
  V2EmptyState,
  V2FormActions,
  V2FormField,
  V2FormGrid,
  V2ToastStack,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { parseRialInput } from '../../lib/finance-display.js';
import type { StockBalancesResponse } from '../../lib/types/inventory.js';
import type { MovementListResponse } from '../../lib/types/inventoryMovements.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import {
  MOVEMENT_STATUS_META,
  STOCK_BALANCES_LIMIT,
  formatQuantity,
  metaOf,
  parseWholeQuantity,
  quantitySign,
  sourceTypeLabel,
  subtractQuantity,
} from './inventoryDisplay.js';
import { useToasts } from './useInventoryUi.js';
import './inventory.css';

type AdjustmentRow = MovementListResponse['movements'][number];

const PICKER_LIMIT = 200;

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Inventory.dc.html §06 ADJUSTMENT — the real
 *   `postCountAdjustment` endpoint (`POST .../inventory/adjustments`)
 *   models a single counted-quantity correction, not a multi-item "count
 *   session" (§05 INVENTORY COUNT) — no session entity exists in the API,
 *   confirmed no controller for it. This page is deliberately the only
 *   one built for both /inventory/adjustments and /inventory/counts:
 *   entering a real counted quantity for one product/warehouse and
 *   letting the server compute the delta IS the real §05 quick-count
 *   interaction, just without a multi-item session wrapper around it.
 * archetype: ENTITY_LIST + sensitive create form
 * status: KIT_CONFORMANT — "دلیل" (reason) has no dedicated backend field;
 *   mapped onto the real, free-text `sourceReference` field and required
 *   client-side, since the board treats it as a mandatory part of the
 *   record. A decrease that would go negative is rejected server-side
 *   (`NegativeStockError`/`InsufficientAvailableStockError`) — no
 *   override path, matching the board's own rule.
 * state reference: ready · empty · permission_denied · network_error
 */
export function AdjustmentsPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [refreshKey, setRefreshKey] = useState(0);
  const { toasts, push, dismiss } = useToasts();

  const [productCode, setProductCode] = useState('');
  const [warehouseCode, setWarehouseCode] = useState('');
  const [locationCode, setLocationCode] = useState('');
  const [lotCode, setLotCode] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [countedQuantity, setCountedQuantity] = useState('');
  const [unitCostRial, setUnitCostRial] = useState('');
  const [reason, setReason] = useState('');
  const [businessDate, setBusinessDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const movements = useApiGet<MovementListResponse>(
    slug ? `${base}/api/v1/inventory/adjustments` : null,
    [slug, refreshKey],
  );
  const products = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/products?limit=${PICKER_LIMIT}` : null,
    [slug],
  );
  const warehouses = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/warehouses?limit=${PICKER_LIMIT}` : null,
    [slug],
  );
  const locations = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/locations?status=active&limit=${PICKER_LIMIT}` : null,
    [slug],
  );
  const balances = useApiGet<StockBalancesResponse>(
    slug ? `${base}/api/v1/inventory/stock-balances?limit=${STOCK_BALANCES_LIMIT}` : null,
    [slug],
  );

  const productOptions = products.status === 'ready' ? products.data.items : [];
  const warehouseOptions = warehouses.status === 'ready' ? warehouses.data.items : [];
  const locationOptions = locations.status === 'ready' ? locations.data.items : [];
  const warehouseIdByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const w of warehouseOptions) map.set(String(w.code), String(w.warehouseId));
    return map;
  }, [warehouseOptions]);
  const locationOptionsForWarehouse = useMemo(() => {
    const warehouseId = warehouseIdByCode.get(warehouseCode);
    if (!warehouseId) return [];
    return locationOptions.filter((l) => String(l.warehouseId) === warehouseId);
  }, [locationOptions, warehouseIdByCode, warehouseCode]);

  // The balance row for exactly the scope being corrected (product · warehouse · location, and no
  // lot/serial unless one is typed — a typed lot/serial has no id here, so the server alone knows it).
  const currentOnHand = useMemo(() => {
    if (balances.status !== 'ready' || !productCode || !warehouseCode) return null;
    if (lotCode.trim() || serialNumber.trim()) return null;
    const locationId = locationOptionsForWarehouse.find(
      (l) => String(l.code) === locationCode,
    )?.warehouseLocationId;
    const row = balances.data.balances.find(
      (b) =>
        b.productCode === productCode &&
        b.warehouseCode === warehouseCode &&
        (!locationCode || b.warehouseLocationId === locationId) &&
        b.lotId === null &&
        b.serialId === null,
    );
    return row ? row.onHand : { numerator: '0', denominator: '1' };
  }, [
    balances,
    locationCode,
    locationOptionsForWarehouse,
    lotCode,
    productCode,
    serialNumber,
    warehouseCode,
  ]);

  const counted = parseWholeQuantity(countedQuantity);
  const delta = currentOnHand && counted ? subtractQuantity(counted, currentOnHand) : null;
  const deltaSign = delta ? quantitySign(delta) : 0;

  if (!slug) return null;

  function resetForm() {
    setProductCode('');
    setWarehouseCode('');
    setLocationCode('');
    setLotCode('');
    setSerialNumber('');
    setCountedQuantity('');
    setUnitCostRial('');
    setReason('');
    setBusinessDate('');
  }

  async function onSubmit() {
    if (!counted) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiPost(
        `${base}/api/v1/inventory/adjustments`,
        {
          productCode,
          warehouseCode,
          locationCode,
          lotCode: lotCode.trim() || undefined,
          serialNumber: serialNumber.trim() || undefined,
          countedQuantity: counted,
          unitCostRial: parseRialInput(unitCostRial) || undefined,
          businessDate,
          sourceReference: reason.trim(),
        },
        { headers: { 'idempotency-key': crypto.randomUUID() } },
      );
      resetForm();
      setRefreshKey((k) => k + 1);
      push('اصلاح موجودی ثبت شد.');
    } catch (err) {
      setError(bannerMessageFrom(err, 'ثبت اصلاح انجام نشد.'));
    } finally {
      setSubmitting(false);
      setConfirming(false);
    }
  }

  function onRequestConfirm(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (
      !productCode ||
      !warehouseCode ||
      !locationCode ||
      !countedQuantity.trim() ||
      !reason.trim() ||
      !businessDate
    ) {
      setError('همهٔ فیلدها از جمله دلیل اصلاح الزامی است.');
      return;
    }
    if (!counted) {
      setError('موجودی شمارش‌شده باید عدد صحیح و نامنفی باشد.');
      return;
    }
    setConfirming(true);
  }

  const productSelectState =
    products.status === 'loading'
      ? { placeholder: 'در حال بارگذاری کالاها…', disabled: true }
      : productOptions.length === 0
        ? { placeholder: 'کالایی در اطلاعات پایه ثبت نشده', disabled: true }
        : { placeholder: '— انتخاب کالا —', disabled: false };
  const warehouseSelectState =
    warehouses.status === 'loading'
      ? { placeholder: 'در حال بارگذاری انبارها…', disabled: true }
      : warehouseOptions.length === 0
        ? { placeholder: 'انباری در اطلاعات پایه ثبت نشده', disabled: true }
        : { placeholder: '— انتخاب انبار —', disabled: false };

  return (
    <div className="inv-page">
      <p className="inv-form__lead">
        حساس‌ترین صفحهٔ انبار — دلیل الزامی است و ثبت با نام شما در سابقه می‌ماند.
      </p>

      <V2Card>
        <V2CardHeader
          icon="warn"
          title="اصلاح جدید"
          actions={<V2Badge tone="warn">اقدام حساس</V2Badge>}
        />
        <form className="inv-form inv-form--card" noValidate onSubmit={onRequestConfirm}>
          <V2FormGrid>
            <V2FormField label="کالا" required width="medium">
              <select
                className="sk-select"
                value={productCode}
                disabled={productSelectState.disabled}
                onChange={(e) => setProductCode(e.target.value)}
              >
                <option value="">{productSelectState.placeholder}</option>
                {productOptions.map((p) => (
                  <option key={String(p.code)} value={String(p.code)}>
                    {String(p.name ?? p.code)}
                  </option>
                ))}
              </select>
            </V2FormField>
            <V2FormField label="انبار" required width="medium">
              <select
                className="sk-select"
                value={warehouseCode}
                disabled={warehouseSelectState.disabled}
                onChange={(e) => {
                  setWarehouseCode(e.target.value);
                  setLocationCode('');
                }}
              >
                <option value="">{warehouseSelectState.placeholder}</option>
                {warehouseOptions.map((w) => (
                  <option key={String(w.code)} value={String(w.code)}>
                    {String(w.name ?? w.code)}
                  </option>
                ))}
              </select>
            </V2FormField>
            <V2FormField label="محل انبار" required width="short">
              <select
                className="sk-select"
                value={locationCode}
                disabled={!warehouseCode}
                title={!warehouseCode ? 'ابتدا انبار را انتخاب کنید' : undefined}
                onChange={(e) => setLocationCode(e.target.value)}
              >
                <option value="">
                  {!warehouseCode ? 'ابتدا انبار را انتخاب کنید' : '— انتخاب محل —'}
                </option>
                {locationOptionsForWarehouse.map((l) => (
                  <option key={String(l.code)} value={String(l.code)}>
                    {String(l.name ?? l.code)}
                  </option>
                ))}
              </select>
            </V2FormField>
            <V2FormField label="تاریخ" required width="medium">
              <JalaliDateInput value={businessDate} onChange={setBusinessDate} />
            </V2FormField>
          </V2FormGrid>

          {currentOnHand ? (
            <div className="inv-kv">
              <span className="inv-kv__label">موجودی فعلی</span>
              <span className="inv-kv__value sk-num">{formatQuantity(currentOnHand)}</span>
            </div>
          ) : null}

          <V2FormGrid>
            <V2FormField label="موجودی شمارش‌شده" required width="short">
              <input
                className="sk-input ltr sk-num"
                inputMode="numeric"
                value={countedQuantity}
                onChange={(e) => setCountedQuantity(e.target.value)}
              />
            </V2FormField>
            {delta ? (
              <V2FormField label="اختلاف" width="short">
                <span
                  className={`inv-delta sk-num ${
                    deltaSign < 0 ? 'inv-delta--down' : deltaSign > 0 ? 'inv-delta--up' : ''
                  }`}
                >
                  {deltaSign > 0 ? '+' : ''}
                  {formatQuantity(delta)}
                </span>
              </V2FormField>
            ) : null}
            <V2FormField label="بچ" width="short">
              <input
                className="sk-input ltr"
                value={lotCode}
                onChange={(e) => setLotCode(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="سریال" width="short">
              <input
                className="sk-input ltr"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="ارزش ریالی واحد (اختیاری در کسری، الزامی در اضافات)" width="medium">
              <input
                className="sk-input ltr sk-num"
                inputMode="numeric"
                value={unitCostRial}
                onChange={(e) => setUnitCostRial(e.target.value)}
                placeholder="ریال"
              />
            </V2FormField>
          </V2FormGrid>

          <V2FormField label="دلیل اصلاح" required width="full">
            <textarea
              className="sk-textarea"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="برای اصلاح موجودی، ثبت دلیل الزامی است"
            />
          </V2FormField>

          {error ? <V2Alert tone="danger">{error}</V2Alert> : null}

          <V2FormActions>
            <V2Button type="submit" variant="primary">
              ثبت اصلاح
            </V2Button>
          </V2FormActions>
        </form>
      </V2Card>

      {movements.status === 'loading' ? <LoadingState /> : null}
      {movements.status === 'error' ? (
        <StatePanel
          variant={movements.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={movements.error.status === 403 ? 'دسترسی ندارید' : 'فهرست قابل نمایش نیست'}
        />
      ) : null}
      {movements.status === 'ready' ? (
        <V2DataGrid
          icon="inventory"
          title="اصلاح‌های موجودی"
          subtitle={`${movements.data.movements.length.toLocaleString('fa-IR')} سند`}
          densitySwitch={false}
          columns={[
            {
              key: 'number',
              header: 'شماره',
              width: 150,
              render: (row: AdjustmentRow) =>
                row.status === 'posted' ? (
                  <Link
                    to={`${base}/inventory/movements/${row.movementId}`}
                    className="ltr sk-num inv-code inv-link"
                  >
                    {row.movementNumber}
                  </Link>
                ) : (
                  <span className="ltr sk-num inv-code">{row.movementNumber}</span>
                ),
            },
            {
              key: 'date',
              header: 'تاریخ',
              width: 120,
              render: (row: AdjustmentRow) => (
                <span className="sk-num">{displayJalaliDate(row.businessDate)}</span>
              ),
            },
            {
              key: 'source',
              header: 'منبع',
              width: 150,
              render: (row: AdjustmentRow) => (
                <span className="sk-tag">{sourceTypeLabel(row.sourceType)}</span>
              ),
            },
            {
              key: 'status',
              header: 'وضعیت',
              width: 120,
              render: (row: AdjustmentRow) => (
                <V2Badge tone={metaOf(MOVEMENT_STATUS_META, row.status).tone}>
                  {metaOf(MOVEMENT_STATUS_META, row.status).label}
                </V2Badge>
              ),
            },
            {
              key: 'actions',
              header: 'چاپ سند',
              width: 110,
              render: (row: AdjustmentRow) => (
                <Link
                  to={`${base}/inventory/movements/${row.movementId}/print`}
                  target="_blank"
                  className="sk-btn sk-btn--ghost sk-btn--sm inv-btn-link"
                  title="چاپ صورتجلسه رسمی تعدیل انبار"
                >
                  <Icon name="invoice" size="sm" />
                  <span>چاپ</span>
                </Link>
              ),
            },
          ]}
          rows={movements.data.movements}
          rowKey={(row) => row.movementId}
          emptyState={
            <V2EmptyState
              icon="inventory"
              title="اصلاحی ثبت نشده"
              description="اصلاح موجودی جدید را از فرم بالا برای یک کالا و انبار ثبت کنید."
              compact
            />
          }
        />
      ) : null}

      <V2ConfirmDialog
        open={confirming}
        title="ثبت قطعی اصلاح موجودی؟"
        message={
          <div className="inv-confirm">
            <span>
              این اصلاح پس از ثبت برگشت‌پذیر نیست و در سابقهٔ انبار با نام شما ثبت می‌شود.
            </span>
            <div className="inv-kv inv-kv--stack">
              <div className="inv-kv__row">
                <span className="inv-kv__label">موجودی فعلی</span>
                <span className="inv-kv__value sk-num">
                  {currentOnHand ? formatQuantity(currentOnHand) : 'نامشخص (سرور محاسبه می‌کند)'}
                </span>
              </div>
              <div className="inv-kv__row">
                <span className="inv-kv__label">پس از اصلاح</span>
                <span className="inv-kv__value sk-num">{formatQuantity(counted)}</span>
              </div>
              <div className="inv-kv__row inv-kv__row--divided">
                <span className="inv-kv__label">دلیل</span>
                <span className="inv-kv__value">{reason}</span>
              </div>
            </div>
          </div>
        }
        danger
        confirmLabel="ثبت قطعی"
        loading={submitting}
        onConfirm={() => void onSubmit()}
        onCancel={() => setConfirming(false)}
      />

      <V2ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
